/**
 * step0.ts — the intro / preloader screen (flow step 0), shown on page load.
 * Centered title + a "Start building" CTA (reads "Loading configurator" until the
 * page is ready), over a large media block (image or video, with mobile overrides).
 * All copy + media come from the section settings (catalog.step0).
 */

import type { Step0Content } from './data';
import { renderDecorIcon } from './icons';
import { wipeInHeading } from './anim';

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };
const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface MountStep0Opts {
  root: HTMLElement;
  content: Step0Content;
  onStart: () => void;
}

export interface Step0Api {
  /** flip the CTA from "Loading configurator" to the enabled button label */
  ready: () => void;
}

export function mountStep0({ root, content, onStart }: MountStep0Opts): Step0Api {
  root.innerHTML = '';

  const wrap = el('div', 'cfg cfg-step0 relative overflow-hidden flex flex-col items-center gap-4 bg-background px-3 md:px-4 pt-18 pb-4');

  // Intro: title + CTA (centered, fixed height).
  const intro = el('div', 'shrink-0 flex flex-col items-center text-center gap-4 pt-14 pb-10');
  const title = el('h1', 'h1 text-pretty m-0 max-w-[20ch]');
  title.textContent = content.title;
  // Loading palette (bg-detail/30) → ready palette (bg-button) animates on ready.
  const btn = el('button',
    'cfg-s0-btn btn btn--primary btn--animate relative overflow-hidden flex items-center justify-center text-sm md:text-sm px-4 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase bg-detail/30 text-foreground') as HTMLButtonElement;
  btn.type = 'button';
  btn.disabled = true;
  // Two stacked lines clipped to one: "Loading configurator" shows first; ready()
  // slides the stack up to reveal the (different) ready label — same motion as the
  // hover wipe-up. After it settles we collapse to the standard btn__label-inner so
  // the hover wipe-up itself keeps working.
  const swap = el('span', 'cfg-s0-swap');
  const line1 = el('span'); line1.textContent = 'Loading configurator';
  const line2 = el('span'); line2.textContent = content.buttonLabel;
  swap.append(line1, line2);
  const btnLabel = el('span', 'btn__label');
  btnLabel.appendChild(swap);
  btn.appendChild(btnLabel);
  btn.addEventListener('click', onStart);
  intro.append(title, btn);

  function toReadyLabel(): void {
    btnLabel.innerHTML = '';
    const inner = el('span', 'btn__label-inner');
    inner.textContent = content.buttonLabel;
    btnLabel.appendChild(inner);
  }

  // Big media — fills the remaining height. Video wins over image; mobile falls back
  // to the mobile asset, then the desktop one.
  const media = el('div', 'relative flex-1 w-full md:col-d-10');
  const mediaInner = el('div', 'w-full h-full relative rounded-sm bg-detail/10 overflow-hidden');
  mediaInner.innerHTML =
    pickMedia(content.video, content.image, content.focalDesktop, 'hidden md:block') +
    pickMedia(content.videoMobile || content.video, content.imageMobile || content.image, content.focalMobile, 'md:hidden');
  media.appendChild(mediaInner);

  // Decorative shape (top-right, bleeds off the edge) — icon + color from settings.
  const decor = el('div', 'absolute top-[-10vw] md:top-[-7vw] right-[2vw] md:right-[-7vw] md:-rotate-30 w-[33vw] md:w-[22vw] max-w-[unset] z-20 pointer-events-none');
  decor.innerHTML = renderDecorIcon(content.icon, content.iconColor);
  media.appendChild(decor);

  wrap.append(intro, media);
  root.appendChild(wrap);
  wipeInHeading(title);

  return {
    ready() {
      btn.disabled = false;
      // animate the palette (bg + text) loading → ready
      btn.classList.remove('bg-detail/30', 'text-foreground');
      btn.classList.add('bg-button', 'text-button-text');
      if (REDUCE) { btn.classList.add('is-ready'); toReadyLabel(); return; }

      // lock the loading width, then animate to the ready label's width while the
      // text slides up (is-ready) and the background colour transitions.
      const cs = getComputedStyle(btn);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const targetW = Math.ceil(line2.getBoundingClientRect().width + padX);
      btn.style.width = btn.offsetWidth + 'px';
      void btn.offsetWidth; // commit the start width before transitioning
      requestAnimationFrame(() => {
        btn.classList.add('is-ready');
        btn.style.width = targetW + 'px';
      });
      const done = (e: TransitionEvent): void => {
        if (e.target !== btn || e.propertyName !== 'width') return;
        btn.removeEventListener('transitionend', done);
        btn.style.width = '';        // back to auto
        toReadyLabel();              // standard label → hover wipe-up works again
      };
      btn.addEventListener('transitionend', done);
    },
  };
}

/** Render a video (preferred) or image filling its container at `vis` breakpoint.
 *  `focal` sets object-position so object-cover crops around the focal point. */
function pickMedia(video: string | null, image: string | null, focal: string, vis: string): string {
  const base = `absolute inset-0 w-full h-full object-cover ${vis}`;
  const pos = `style="object-position:${focal}"`;
  if (video) return `<video class="${base}" ${pos} autoplay muted loop playsinline src="${video}"></video>`;
  if (image) return `<img class="${base}" ${pos} src="${image}" alt="">`;
  return '';
}
